/**
 * @module routes.Sessions.AllCoachesPublicSession._components.byDateAvailability
 *
 * Derivations behind the "Book a Session / By Date" screen (frames 526:29419 and
 * 526:29894): which days the calendar may offer, which day it opens on, the
 * facility-wide slot list for one day, and the booking options behind a slot.
 *
 * The facility view is assembled client-side. `/available_dates` takes every
 * coach id at once and returns each profile's dates, but slots come from a
 * per-coach endpoint (`/coach_profiles/:id/available_times`), so a day's slots
 * are the union of one call per coach and the "2 Coaches" count under each slot
 * is how many of those calls produced that time.
 */
import { DateTime } from "luxon";

import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { AvailableSlotModel } from "app/models/scheduling/AvailableSlotModel";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import {
  PackageCredit,
  getApplicablePackageCredit,
} from "app/utils/creditBundles";

import { bookableRates, orderRatesForAthlete } from "./coachMeta";

/** One `/available_dates` entry: a coach and the days they can be booked. */
export interface CoachDatesResponse {
  dates?: string[];
}

/** A time offered by at least one coach, with who is free then. */
export interface FacilitySlot {
  /** `startHour` in millis — stable across coaches, so it is the grouping key. */
  timeId: number;
  startHour: DateTime;
  /** Coach `id`s free at this time, in the order the coaches were passed in. */
  coachIds: string[];
  /**
   * The booking options of each coach that offered this time. Set when the
   * availability was asked per option (the Dates list). A time that one option
   * offers is not a time for the other options of the coach: an option whose
   * every bay is taken does not offer it, and a group option with a seat left
   * offers a time that the coach's other options do not.
   */
  rateIdsByCoach?: Record<string, number[]>;
}

export type PartOfDay = "morning" | "afternoon" | "evening";

/** A coach's bookable options at the selected time, as the cards render them. */
export interface CoachOptionGroup {
  coach: CoachProfileModel;
  options: Array<{
    rate: LessonRateModel;
    /** Set when a held package covers this rate — the card shows it instead of the price. */
    packageCredit?: PackageCredit;
  }>;
}

/**
 * Union of every coach's available days. `/available_dates` answers per profile,
 * and the calendar offers a day when *anyone* is free, so the sets merge.
 */
export const unionAvailableDates = (
  profiles: CoachDatesResponse[],
): Set<string> => {
  const dates = new Set<string>();
  (profiles || []).forEach((profile) => {
    (profile?.dates || []).forEach((raw) => {
      const iso = DateTime.fromISO(raw).toISODate();
      if (iso) {
        dates.add(iso);
      }
    });
  });
  return dates;
};

/**
 * The day the screen opens on: the earliest available day that is not in the
 * past (comment #30 — "default to the next available day"). Returns null when
 * the facility has nothing left to offer, which the caller shows as an empty
 * state rather than landing on a day with no slots.
 */
export const nextAvailableDate = (
  availableDates: Set<string>,
  from: DateTime,
): string | null => {
  const today = from.toISODate();
  if (!today) {
    return null;
  }
  // ISO dates are fixed-width, so lexical order is chronological order.
  const upcoming = Array.from(availableDates)
    .filter((date) => date >= today)
    .sort();
  return upcoming[0] ?? null;
};

/**
 * Collapse per-coach slot lists into one facility-wide list.
 *
 * Keyed on the slot's start instant rather than its wall-clock label so two
 * coaches only share a slot when they are genuinely free at the same moment.
 */
export const aggregateSlots = (
  perCoachSlots: Array<{
    coachId: string;
    slots: AvailableSlotModel[];
    /** The booking option the slots were asked for, when asked per option. */
    rateId?: number;
  }>,
): FacilitySlot[] => {
  const byTime = new Map<number, FacilitySlot>();

  perCoachSlots.forEach(({ coachId, slots, rateId }) => {
    (slots || []).forEach((slot) => {
      let entry = byTime.get(slot.timeId);
      if (!entry) {
        entry = {
          timeId: slot.timeId,
          startHour: slot.startHour,
          coachIds: [],
        };
        byTime.set(slot.timeId, entry);
      }
      // A coach can expose the same start time more than once (two rates of
      // different lengths); they are still one coach at that time.
      if (!entry.coachIds.includes(coachId)) {
        entry.coachIds.push(coachId);
      }
      if (rateId !== undefined) {
        const byCoach = entry.rateIdsByCoach || {};
        const rateIds = byCoach[coachId] || [];
        if (!rateIds.includes(rateId)) {
          byCoach[coachId] = [...rateIds, rateId];
        }
        entry.rateIdsByCoach = byCoach;
      }
    });
  });

  return Array.from(byTime.values()).sort((a, b) => a.timeId - b.timeId);
};

/**
 * Morning / Afternoon / Evening, the three headings in the frame. Noon starts
 * the afternoon and 5 PM starts the evening.
 */
export const partOfDay = (startHour: DateTime): PartOfDay => {
  const hour = startHour.hour;
  if (hour < 12) {
    return "morning";
  }
  return hour < 17 ? "afternoon" : "evening";
};

/** Slots split into the frame's three groups, each keeping chronological order. */
export const groupSlotsByPartOfDay = (
  slots: FacilitySlot[],
): Array<{ part: PartOfDay; slots: FacilitySlot[] }> => {
  const order: PartOfDay[] = ["morning", "afternoon", "evening"];
  return order
    .map((part) => ({
      part,
      slots: slots.filter((slot) => partOfDay(slot.startHour) === part),
    }))
    .filter((group) => group.slots.length > 0);
};

/**
 * The options behind a selected slot, grouped by coach the way the frame stacks
 * them. Only coaches free at that time contribute, and only rates an athlete
 * can actually pay for. When the slot records which options offered it
 * (`rateIdsByCoach`), only those options are listed.
 *
 * Within a coach the options are in the same order as everywhere else the
 * athlete meets them (#31, `orderRatesForAthlete`): the ones a package they hold
 * covers first, then the coach's own order, then the ones needing a package they
 * do not have.
 */
export const optionsForSlot = (
  coaches: CoachProfileModel[],
  slot: FacilitySlot | null,
  packageCredits?: Map<number, PackageCredit>,
): CoachOptionGroup[] => {
  if (!slot) {
    return [];
  }
  const offeredAt = (coach: CoachProfileModel) => {
    const rates = bookableRates(coach);
    if (!slot.rateIdsByCoach) {
      return rates;
    }
    const rateIds = slot.rateIdsByCoach[coach.id] || [];
    return rates.filter((rate) => rateIds.includes(rate.id));
  };

  return coaches
    .filter((coach) => slot.coachIds.includes(coach.id))
    .map((coach) => ({
      coach,
      options: orderRatesForAthlete(offeredAt(coach), packageCredits).map(
        (rate) => ({
          rate,
          packageCredit: getApplicablePackageCredit(rate, packageCredits),
        }),
      ),
    }))
    .filter((group) => group.options.length > 0);
};

/** Total across the groups — the "7 options" heading above the cards. */
export const countOptions = (groups: CoachOptionGroup[]): number =>
  groups.reduce((total, group) => total + group.options.length, 0);
